import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '@/styles/createUserStyles.scss';
import { useNavigate } from 'react-router-dom';
import { dashboardOptions } from '@/constants/dashboardOptions';
import SignatureUpload from '@/components/SignatureUpload';
import { FaUser, FaLock, FaPen, FaBell, FaEye } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

const UserProfile = () => {
    const { user, apiFetch, logout, refreshUser, permissions } = useAuth();
    const [firstName, setFirstName] = useState(user?.FirstName || user?.firstName || '');
    const [lastName, setLastName] = useState(user?.LastName || user?.lastName || '');
    const [phoneNumber, setPhoneNumber] = useState(user?.PhoneNumber || user?.phoneNumber || '');
    const [email, setEmail] = useState(user?.UserEmail || user?.userEmail || '');
    const [homeDashboardRoute, setHomeDashboardRoute] = useState(user?.HomeDashboardRoute || user?.homeDashboardRoute || '/home');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [existingSignature, setExistingSignature] = useState(null);
    const [loadingSignature, setLoadingSignature] = useState(true);

    // Change-password form state — kept separate from profile fields so the two
    // forms can be submitted and validated independently.
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    // Controls visibility of the Change Password form.
    // Hidden by default — the user must explicitly opt in to avoid accidental changes.
    const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);

    // User preferences state
    const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
    const [defaultActionItemView, setDefaultActionItemView] = useState('');
    const [defaultSwimGroupBy, setDefaultSwimGroupBy] = useState('');
    const [savingPreferences, setSavingPreferences] = useState(false);

    const navigate = useNavigate();

    /**
     * Load user preferences on mount
     */
    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const userId = user?.UserID || user?.userID;
                if (!userId) return;
                const res = await apiFetch(`/api/UserPreferences/${userId}`);
                const data = await res.json();
                if (data.success && data.data) {
                    setEmailNotificationsEnabled(data.data.emailNotificationsEnabled ?? true);
                    setDefaultActionItemView(data.data.defaultActionItemView || '');
                    setDefaultSwimGroupBy(data.data.defaultSwimGroupBy || '');
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Preferences not saved yet — use defaults
            }
        };
        loadPreferences();
    }, [apiFetch, user]);

    // Track changes to avoid auto-saving on mount.
    // Counter: 0 = not loaded, 1 = initial load applied (skip), 2+ = user changed.
    const preferencesChangeCountRef = useRef(0);

    // Auto-save preferences when they change after initial load
    useEffect(() => {
        preferencesChangeCountRef.current += 1;
        // Skip the first change (initial API load populating state)
        if (preferencesChangeCountRef.current <= 1) return;

        const timer = setTimeout(() => {
            handleSavePreferences();
        }, 500);
        return () => clearTimeout(timer);
    }, [emailNotificationsEnabled, defaultActionItemView, defaultSwimGroupBy]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Filter dashboard options to only show those the user has access to.
     * System Default (/home) is always included regardless of explicit permissions,
     * as /home is accessible to all authenticated users.
     * Other options are only shown if the user has permission to access that pageRoute.
     */
    const availableDashboardOptions = dashboardOptions.filter(option => {
        // Always include System Default option (/home)
        if (option.value === '/home') return true;
        
        // Check if user has permission for this dashboard's route
        return permissions.some(permission => permission.pageRoute === option.value);
    });

    /**
     * Load existing signature on component mount
     */
    useEffect(() => {
        const loadSignature = async () => {
            try {
                const response = await apiFetch('/api/User/signature');
                const data = await response.json();
                if (data.success && data.data) {
                    setExistingSignature(data.data.signatureData);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // No signature exists yet, that's okay
                console.log('No signature found or error loading signature');
            } finally {
                setLoadingSignature(false);
            }
        };

        loadSignature();
    }, [apiFetch]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!firstName || !lastName || !email) {
            toast.error('First name, last name, and email are required.');
            return;
        }
        setIsLoading(true);
        try {
            const response = await apiFetch('/api/User/update-own', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstName,
                    lastName,
                    phoneNumber,
                    email,
                    homeDashboardRoute
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Profile updated successfully!');
                // Refresh user data in context so changes take effect immediately
                await refreshUser();
            } else {
                toast.error(data.message || 'Failed to update profile.');
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            toast.error('An error occurred while updating profile.');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Handle signature save
     */
    const handleSignatureSave = async (signatureData) => {
        try {
            const response = await apiFetch('/api/User/signature', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(signatureData)
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Signature saved successfully!');
                setExistingSignature(signatureData.signatureData);
            } else {
                toast.error(data.message || 'Failed to save signature.');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving signature:', error);
            toast.error('An error occurred while saving signature.');
        }
    };

    /**
     * Handle changing the user's own password.
     * Validates that new password and confirmation match before calling the API.
     */
    const handleChangePassword = async (e) => {
        e.preventDefault();

        // Client-side validation before hitting the network
        if (!currentPassword) {
            toast.error('Please enter your current password.');
            return;
        }
        if (!newPassword || newPassword.length < 8) {
            toast.error('New password must be at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('New password and confirmation do not match.');
            return;
        }

        setIsChangingPassword(true);
        try {
            const response = await apiFetch('/api/User/change-own-password', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ currentPassword, newPassword })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Password changed successfully!');
                // Clear the form and collapse it after a successful change
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setShowChangePasswordForm(false);
            } else {
                toast.error(data.message || 'Failed to change password.');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error changing password:', error);
            toast.error('An error occurred while changing password.');
        } finally {
            setIsChangingPassword(false);
        }
    };

    /**
     * Save user preferences (notifications, default view)
     */
    const handleSavePreferences = async () => {
        setSavingPreferences(true);
        try {
            const userId = user?.UserID || user?.userID;
            const response = await apiFetch(`/api/UserPreferences/${userId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emailNotificationsEnabled,
                    defaultActionItemView: defaultActionItemView || null,
                    defaultSwimGroupBy: defaultSwimGroupBy || null
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Preferences saved successfully!');
            } else {
                toast.error(data.message || 'Failed to save preferences.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while saving preferences.');
        } finally {
            setSavingPreferences(false);
        }
    };

    /**
     * Toggles the Change Password form.
     * Clears any partially entered values when hiding the form so stale data
     * does not persist if the user cancels and re-opens later.
     */
    const handleToggleChangePassword = () => {
        const WillShow = !showChangePasswordForm;
        setShowChangePasswordForm(WillShow);
        if (!WillShow) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        }
    };

    if (sessionExpired) {
        return (
            <div className="error-message">
                <p>Your session has expired. Please log in again.</p>
                <button onClick={() => navigate('/')}>Go to Login</button>
            </div>
        );
    }

    return (
        <div className="create-user">
            {/* Visually hidden via .create-user-title { display: none } in CSS.
                Kept in the DOM so existing Playwright tests can assert on text content
                without needing a visible heading selector. */}
            <div className="create-user-title">My Profile</div>

            {/* ── Page header ── */}
            <div className="create-user-header">
                <div className="header-content">
                    <h2>
                        <FaUser className="header-icon" />
                        My Profile
                    </h2>
                </div>
            </div>

            <div className="create-user-content">

                {/* ── Personal Information section ── */}
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <div className="section-header">
                            <FaUser className="section-icon" />
                            Personal Information
                        </div>
                        <div className="create-user-fields">
                            <div id="create-user-fullname">
                                <div className="create-user-names">
                                    <label htmlFor="firstname">First Name*</label>
                                    <input
                                        type="text"
                                        id="firstname"
                                        value={firstName}
                                        onChange={e => setFirstName(e.target.value)}
                                    />
                                </div>
                                <div className="create-user-names">
                                    <label htmlFor="lastname">Last Name*</label>
                                    <input
                                        type="text"
                                        id="lastname"
                                        value={lastName}
                                        onChange={e => setLastName(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="create-user-phone">
                                <label htmlFor="phonenumber">Phone Number</label>
                                <input
                                    type="text"
                                    id="phonenumber"
                                    value={phoneNumber}
                                    onChange={e => setPhoneNumber(e.target.value)}
                                />
                            </div>
                            <div className="create-user-email">
                                <label htmlFor="email">Email*</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    disabled
                                />
                            </div>
                            <div className="create-user-email">
                                <label htmlFor="homeDashboard">Home Page</label>
                                <select
                                    id="homeDashboard"
                                    value={homeDashboardRoute}
                                    onChange={e => setHomeDashboardRoute(e.target.value)}
                                >
                                    {availableDashboardOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                                    Select which dashboard to show when clicking the logo. Only dashboards you have access to are shown.
                                </small>
                            </div>
                        </div>
                        <div className="create-user-buttons">
                            <button
                                className="button-secondary"
                                type="button"
                                onClick={() => navigate('/home')}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </form>

                {/* ── Change Password section ── collapsed by default ── */}
                <div className="form-section">
                    <div className="section-header">
                        <FaLock className="section-icon" />
                        Change Password
                    </div>

                    {/* When collapsed, show a brief description and the toggle button */}
                    {!showChangePasswordForm && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ color: '#718096', fontSize: '0.9rem' }}>
                                Update your account password
                            </span>
                            <button
                                type="button"
                                className="change-password-toggle"
                                onClick={handleToggleChangePassword}
                            >
                                Change Password
                            </button>
                        </div>
                    )}

                    {/* Password fields are only rendered when the user explicitly requests a change */}
                    {showChangePasswordForm && (
                        <form onSubmit={handleChangePassword}>
                            <div className="create-user-fields">
                                <div className="create-user-phone">
                                    <label htmlFor="currentPassword">Current Password*</label>
                                    <input
                                        type="password"
                                        id="currentPassword"
                                        value={currentPassword}
                                        onChange={e => setCurrentPassword(e.target.value)}
                                        placeholder="Enter your current password"
                                        autoComplete="current-password"
                                    />
                                </div>
                                <div className="create-user-phone">
                                    <label htmlFor="newPassword">New Password*</label>
                                    <input
                                        type="password"
                                        id="newPassword"
                                        value={newPassword}
                                        onChange={e => setNewPassword(e.target.value)}
                                        placeholder="At least 8 characters"
                                        autoComplete="new-password"
                                    />
                                </div>
                                <div className="create-user-phone">
                                    <label htmlFor="confirmPassword">Confirm New Password*</label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        placeholder="Re-enter new password"
                                        autoComplete="new-password"
                                    />
                                </div>
                                <div className="create-user-buttons">
                                    <button
                                        className="button-secondary"
                                        type="button"
                                        onClick={handleToggleChangePassword}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isChangingPassword}
                                    >
                                        {isChangingPassword ? 'Changing...' : 'Save New Password'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                {/* ── Notification & View Preferences section ── */}
                <div className="form-section">
                    <div className="section-header">
                        <FaBell className="section-icon" />
                        Notification Preferences
                    </div>
                    <div className="create-user-fields">
                        <div className="create-user-phone" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <label htmlFor="emailNotifications" style={{ flex: 1, margin: 0 }}>
                                Email Notifications
                                <small style={{ color: '#718096', display: 'block', fontWeight: 'normal', marginTop: '4px' }}>
                                    Receive email notifications when action items relevant to you are created or updated.
                                    Note: @mention notifications are always sent regardless of this setting.
                                </small>
                            </label>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    id="emailNotifications"
                                    checked={emailNotificationsEnabled}
                                    onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                        </div>
                    </div>

                    {savingPreferences && (
                        <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>Saving...</small>
                    )}

                    <div className="section-header" style={{ marginTop: '24px' }}>
                        <FaEye className="section-icon" />
                        Default View Preferences
                    </div>
                    <div className="create-user-fields">
                        <div className="create-user-phone">
                            <label htmlFor="defaultView">Default Action Items View</label>
                            <select
                                id="defaultView"
                                value={defaultActionItemView}
                                onChange={(e) => setDefaultActionItemView(e.target.value)}
                            >
                                <option value="">System Default (Kanban)</option>
                                <option value="kanban">Kanban</option>
                                <option value="swimlanes">Swimlanes</option>
                                <option value="list">List</option>
                            </select>
                        </div>
                        <div className="create-user-phone">
                            <label htmlFor="defaultGroupBy">Default Swimlane Group By</label>
                            <select
                                id="defaultGroupBy"
                                value={defaultSwimGroupBy}
                                onChange={(e) => setDefaultSwimGroupBy(e.target.value)}
                            >
                                <option value="">Default (Function)</option>
                                <option value="function">Function</option>
                                <option value="property">Property</option>
                                <option value="assignee">Assignee</option>
                                <option value="type">Type</option>
                            </select>
                            <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                                Choose how swimlanes are grouped when you open the Action Items board.
                            </small>
                        </div>
                    </div>
                </div>

                {/* ── Signature for PDF Signing section ── */}
                <div className="form-section">
                    <div className="section-header">
                        <FaPen className="section-icon" />
                        Signature for PDF Signing
                    </div>
                    {loadingSignature ? (
                        <div style={{ padding: '2em', textAlign: 'center', color: '#666' }}>
                            Loading signature...
                        </div>
                    ) : (
                        <SignatureUpload
                            onSignatureSave={handleSignatureSave}
                            existingSignature={existingSignature}
                        />
                    )}
                </div>

            </div>


        </div>
    );
};

export default UserProfile;
